import React, { useId } from 'react';
import {
  TEMPLE_COLUMN_CENTERS,
  TEMPLE_DATUMS,
  TEMPLE_FOUNDATION_RECTS,
  getTempleColumnDetails,
  getTempleColumnShaft,
  getTempleInnerColumnShaft,
  getTemplePedimentPath,
  getTempleRoofBeam,
  getTempleTympanumPath,
} from './BadgeTempleGeometry';
import { roundedBadgePercentageValue } from './badgePercentageFormatting';

const Badge = ({ 
  rates, 
  starCount = 0, 
  size = 'small', 
  getShimmerClass, 
  getStars, 
  getCentralShape, 
  getFlameClass,
  // Volume PR stats with week/month/year horizons
  volumePRs = { week: { prRate: 0 }, month: { prRate: 0 }, year: { prRate: 0 } },
  nutritionCompliance = { month: 0 }
}) => {
  const className = size === 'large' ? 'badge-svg-large' : 'badge-svg';
  const uniqueId = useId();
  const gradientId = `glint-gradient-${uniqueId}`;
  const arrowsClipId = `arrows-clip-${uniqueId}`;
  const arrowSymbolId = `arrow-symbol-${uniqueId}`;
  const arrowGradId = `arrow-grad-${uniqueId}`;
  const arrowGlowId = `arrow-glow-${uniqueId}`;
  const discFaceId = `disc-face-${uniqueId}`;
  const discClipId = `disc-clip-${uniqueId}`;
  const rimMetalId = `rim-metal-${uniqueId}`;
  const rimSheenId = `rim-sheen-${uniqueId}`;
  const discVignetteId = `disc-vignette-${uniqueId}`;
  const groundId = `ground-${uniqueId}`;
  const columnShadeId = `column-shade-${uniqueId}`;
  const roofShadeId = `roof-shade-${uniqueId}`;
  const stepShadeId = `step-shade-${uniqueId}`;
  const glint2Id = `glint2-gradient-${uniqueId}`;
  const arrowTrailId = `arrow-trail-${uniqueId}`;
  const specularId = `specular-${uniqueId}`;
  const contentId = `content-${uniqueId}`;
  const shineMaskId = `shine-mask-${uniqueId}`;

  const starData = getStars(starCount) || { items: [], bounds: null };
  const stars = starData.items || [];
  const weekRate = roundedBadgePercentageValue(rates?.week);
  const monthRate = roundedBadgePercentageValue(rates?.month);
  const yearRate = roundedBadgePercentageValue(rates?.year);

  const shimmerClass = getShimmerClass(monthRate);
  const hasShimmer = shimmerClass && shimmerClass.includes('shimmer');
  const isForgedObsidian = shimmerClass && shimmerClass.includes('shimmer-obsidian');
  // Gold and obsidian retain a hard specular sweep, but the old decorative
  // sparkle field is intentionally gone: top status comes from material weight.
  const hasPremiumSpecular = isForgedObsidian || (shimmerClass && shimmerClass.includes('shimmer-gold'));
  const centralShape = yearRate >= 0 ? getCentralShape(yearRate) : null;
  const glintStopColor = isForgedObsidian ? '#f8fafc' : 'white';

  // The medallion rim carries the same tier as the shimmer, so the frame and the
  // totem always agree about what the athlete has earned.
  const rimStops = (() => {
    const cls = shimmerClass || '';
    if (cls.includes('shimmer-obsidian')) {
      return ['#f8fafc', '#66717e', '#090b0f', '#d6ad3b', '#20262e', '#aeb7c2'];
    }
    if (cls.includes('shimmer-gold')) {
      return ['#fff4c4', '#fbbf24', '#8a5a08', '#fcd34d', '#a16207', '#ffe9a8'];
    }
    if (cls.includes('shimmer-silver')) {
      return ['#ffffff', '#dbe2ea', '#5b6878', '#eef2f6', '#7b8896', '#f8fafc'];
    }
    if (cls.includes('shimmer-bronze')) {
      return ['#f6c99b', '#c2691f', '#5e2f08', '#e08a45', '#7c3d09', '#f0b27a'];
    }
    return ['#5a6a80', '#3a4557', '#1c2430', '#4a5769', '#232c3a', '#55647a'];
  })();
  
  // Volume PR animated background properties
  const weekPRRate = roundedBadgePercentageValue(volumePRs?.week?.prRate);
  const monthPRRate = roundedBadgePercentageValue(volumePRs?.month?.prRate);
  const yearPRRate = roundedBadgePercentageValue(volumePRs?.year?.prRate);
  
  // Year rate → Arrow color (thresholds: 30/60/90/95%)
  const getArrowColor = (rate) => {
    if (rate >= 95) return '#f8fafc'; // Platinum - apex
    if (rate >= 90) return '#fbbf24'; // Gold - exceptional
    if (rate >= 60) return '#a3e635'; // Lime - strong
    if (rate >= 30) return '#60a5fa'; // Blue - good
    return 'rgba(255, 255, 255, 0.25)'; // Grey - no color yet
  };
  const arrowColor = getArrowColor(yearPRRate);
  const hasArrowBackground = weekPRRate >= 30 || monthPRRate >= 30 || yearPRRate >= 30;
  
  // Week rate → Speed & number of arrows
  const getArrowConfig = (rate) => {
    if (rate >= 95) return { count: 12, duration: 1.5 };  // Fast, many arrows
    if (rate >= 90) return { count: 10, duration: 2 };
    if (rate >= 60) return { count: 7, duration: 2.5 };
    if (rate >= 30) return { count: 4, duration: 3.5 };
    return { count: 2, duration: 5 }; // Slow, few arrows
  };
  const arrowConfig = getArrowConfig(weekPRRate);
  
  // Month rate → Arrow size (length / width of the pennant shape)
  const getArrowSize = (rate) => {
    if (rate >= 95) return { length: 12, width: 5.5 };
    if (rate >= 90) return { length: 10.5, width: 4.8 };
    if (rate >= 60) return { length: 9, width: 4.2 };
    if (rate >= 30) return { length: 7.5, width: 3.6 };
    return { length: 6, width: 3 };
  };
  const arrowStyle = getArrowSize(monthPRRate);

  // Swept dart: needle tip, barbs that curve back off the shoulders, and a
  // long thin tail. Anchored so (0,0) sits at the arrow's vertical center.
  const arrowPath = (() => {
    const L = arrowStyle.length;
    const W = arrowStyle.width;
    const tip = -L * 0.58;
    const barb = -L * 0.02;
    const waist = -L * 0.12;
    const tail = L * 0.55;
    return [
      `M 0 ${tip}`,
      `C ${W * 0.30} ${tip + L * 0.30} ${W * 0.46} ${barb - L * 0.14} ${W * 0.5} ${barb}`,
      `L ${W * 0.15} ${waist}`,
      `L ${W * 0.09} ${tail}`,
      `L 0 ${tail + L * 0.1}`,
      `L ${-W * 0.09} ${tail}`,
      `L ${-W * 0.15} ${waist}`,
      `L ${-W * 0.5} ${barb}`,
      `C ${-W * 0.46} ${barb - L * 0.14} ${-W * 0.30} ${tip + L * 0.30} 0 ${tip}`,
      'Z'
    ].join(' ');
  })();
  
  // Generate arrow positions (distributed across the badge area)
  const generateArrowPositions = (count) => {
    const positions = [];
    for (let i = 0; i < count; i++) {
      // Step by a co-prime stride so successive arrows land far apart instead
      // of stacking into the five columns a modulo spread would give.
      const x = 7 + ((i * 37) % 46) + Math.sin(i * 2.3) * 2.5;
      const startY = 60 + (i * 17) % 40; // Stagger start positions
      const delay = (i * 0.3) % arrowConfig.duration;
      positions.push({ x, startY, delay });
    }
    return positions;
  };
  
  const arrowPositions = generateArrowPositions(arrowConfig.count);
  
  // Nutrition compliance visual: Temple structure (Foundation/Pillars/Roof)
  // Foundation = week, Pillars = month, Roof = year
  const weekNutrition = roundedBadgePercentageValue(nutritionCompliance?.week?.complianceRate);
  const monthNutrition = roundedBadgePercentageValue(nutritionCompliance?.month?.complianceRate);
  const yearNutrition = roundedBadgePercentageValue(nutritionCompliance?.year?.complianceRate);
  
  // Temple tier calculations
  const getTempleTier = (level) => {
    if (level >= 90) return 4; // Sacred/Crystal/Bedrock
    if (level >= 70) return 3; // Dome/Marble/Solid
    if (level >= 50) return 2; // Beams/Stone/Laid
    if (level >= 30) return 1; // Partial/Wood/Rough
    return 0;
  };

  // Roof uses 13 tiers matching the rounded monthly stage cutoffs.
  // 0=Open Sky, 1=Roof Frame, 2=Partial Thatch, 3=Thatch Roof, 4=Shingled Roof,
  // 5=Stone Tiles, 6=Heavy Stone, 7=Stone Roof, 8=Marble Roof, 9=Marble Dome,
  // 10=Grand Dome, 11=Apex Monument, 12=Platinum Monument
  const getRoofTier = (level) => {
    if (level >= 100) return 12;
    if (level >= 92) return 11;
    if (level >= 83) return 10;
    if (level >= 75) return 9;
    if (level >= 67) return 8;
    if (level >= 58) return 7;
    if (level >= 50) return 6;
    if (level >= 42) return 5;
    if (level >= 33) return 4;
    if (level >= 25) return 3;
    if (level >= 17) return 2;
    if (level >= 8) return 1;
    return 0;
  };
  
  const foundationTier = getTempleTier(weekNutrition);
  const pillarTier = getTempleTier(monthNutrition);
  const roofTier = getRoofTier(yearNutrition);
  
  // Foundation colors and styles (weekly)
  const getFoundationStyle = (tier) => {
    const styles = [
      { fill: 'transparent', stroke: 'transparent', glow: 'none' },
      { fill: '#525252', stroke: '#404040', glow: 'none' }, // Rough stone
      { fill: '#737373', stroke: '#525252', glow: 'none' }, // Laid stone
      { fill: '#a1a1aa', stroke: '#71717a', glow: 'brightness(1.2)' }, // Solid marble
      { fill: 'url(#temple-foundation-gradient)', stroke: '#d6ad3b', glow: 'brightness(1.08) contrast(1.12)' } // Black granite with forged-gold seam
    ];
    return styles[tier];
  };
  
  // Pillar colors and styles (monthly)
  const getPillarStyle = (tier) => {
    const styles = [
      { fill: 'transparent', stroke: 'transparent', glow: 'none', capital: false },
      { fill: '#78716c', stroke: '#57534e', glow: 'none', capital: false }, // Wood
      { fill: '#a8a29e', stroke: '#78716c', glow: 'none', capital: true }, // Stone
      { fill: '#e7e5e4', stroke: '#d6d3d1', glow: 'brightness(1.15)', capital: true }, // Marble
      { fill: 'url(#temple-pillar-gradient)', stroke: '#d6ad3b', glow: 'brightness(1.08) contrast(1.12)', capital: true } // Forged steel
    ];
    return styles[tier];
  };
  
  // Roof colors and styles (yearly) - 13 tiers matching modal levels
  // Structural eras: 0 open, 1-2 rafters/frame, 3-4 thatch/wood, 5-6 heavy stone, 7-8 stone/marble, 9-10 marble dome, 11-12 sacred/divine
  const getRoofStyle = (tier) => {
    if (tier >= 12) return { fill: 'url(#temple-roof-perfect-gradient)', stroke: '#f8fafc', glow: 'brightness(1.16) contrast(1.12)', ornate: true, divine: true };   // 100% Platinum Monument
    if (tier >= 11) return { fill: 'url(#temple-roof-gradient)', stroke: '#d6ad3b', glow: 'brightness(1.08) contrast(1.1)', ornate: true, divine: false };  // 92% Apex Monument
    if (tier >= 10) return { fill: '#e4e4e7', stroke: '#a1a1aa', glow: 'brightness(1.25)', ornate: true, divine: false };   // 83% Grand Dome
    if (tier >= 9)  return { fill: '#d4d4d8', stroke: '#a1a1aa', glow: 'brightness(1.2)', ornate: true, divine: false };    // 75% Marble Dome
    if (tier >= 8)  return { fill: '#c4c4c9', stroke: '#a1a1aa', glow: 'brightness(1.15)', ornate: false, divine: false };  // 67% Marble Roof
    if (tier >= 7)  return { fill: '#a8a8ad', stroke: '#71717a', glow: 'brightness(1.1)', ornate: false, divine: false };   // 58% Stone Roof
    if (tier >= 6)  return { fill: '#91919a', stroke: '#71717a', glow: 'none', ornate: false, divine: false };              // 50% Heavy Stone
    if (tier >= 5)  return { fill: '#7a7a85', stroke: '#525260', glow: 'none', ornate: false, divine: false };              // 42% Stone Tiles
    if (tier >= 4)  return { fill: '#96794f', stroke: '#735a38', glow: 'none', ornate: false, divine: false };              // 33% Shingled Roof
    if (tier >= 3)  return { fill: '#7e6440', stroke: '#5e4a2e', glow: 'none', ornate: false, divine: false };              // 25% Thatch Roof
    if (tier >= 2)  return { fill: '#6e5840', stroke: '#50402e', glow: 'none', ornate: false, divine: false };              // 17% Partial Thatch
    if (tier >= 1)  return { fill: '#64503a', stroke: '#483828', glow: 'none', ornate: false, divine: false };              // 8% Roof Frame
    return { fill: 'transparent', stroke: 'transparent', glow: 'none', ornate: false, divine: false };                      // 0% Open Sky
  };
  
  const foundationStyle = getFoundationStyle(foundationTier);
  const pillarStyle = getPillarStyle(pillarTier);
  const roofStyle = getRoofStyle(roofTier);
  
  const hasTemple = weekNutrition >= 30 || monthNutrition >= 30 || yearNutrition >= 8;
  const isTempleComplete = foundationTier >= 4 && pillarTier >= 4 && roofTier >= 11;
  const templeGradientId = `temple-gradients-${uniqueId}`;

  const pillarFill = pillarTier >= 4 ? `url(#${templeGradientId}-pillar)` : pillarStyle.fill;
  const foundationFill = foundationTier >= 4 ? `url(#${templeGradientId}-foundation)` : foundationStyle.fill;

  // Shared vertical datums for the temple scene, in the pre-scale 60x60 space.
  // Everything keys off these so a tier can never leave a piece floating:
  // the entablature always ends at 18.5, every shaft always reaches 46.2, and
  // the stylobate always catches them at 45.7.
  const BEAM_BOTTOM = TEMPLE_DATUMS.beamBottom;

  // Pediment outline. Each era springs from the top of its own entablature, so
  // the gable and the beam share an edge instead of overlapping by guesswork.
  const pedimentPath = getTemplePedimentPath(roofTier);
  const tympanumPath = getTempleTympanumPath(roofTier);

  // Custom heraldic totems use the field confidently, but long-term stars own
  // the foreground when present so neither the row nor ring collides with the crest.
  const hasStarRing = stars.length > 5;
  const hasStarRow = stars.length > 0 && !hasStarRing;
  const totemScale = hasTemple
    ? (hasStarRing ? 0.78 : hasStarRow ? 0.96 : 1.04)
    : (hasStarRing ? 0.88 : hasStarRow ? 1.24 : 1.42);
  const totemTransform = `translate(30 ${hasTemple ? 32 : 31}) scale(${totemScale}) translate(-30 -30)`;

  // The shaft is symmetric, so one generator serves both sides; the cylindrical
  // shading comes from a bounding-box gradient and mirrors itself for free.
  const columnShaft = c => getTempleColumnShaft(c, pillarTier);

  const renderColumn = (c, key) => {
    const shaft = columnShaft(c);
    const details = getTempleColumnDetails(c);
    return (
      <g className="temple-column" key={key}>
        <path d={shaft} fill={pillarFill} stroke={pillarStyle.stroke} strokeWidth="0.3" />
        <path d={shaft} fill={`url(#${columnShadeId})`} />

        {/* Fluting. The grooves splay slightly to follow the taper. */}
        {pillarTier >= 3 && (
          <path
            d={`M ${c - 1.15} 22 L ${c - 1.3} 44.4 M ${c} 21.8 L ${c} 44.7 M ${c + 1.15} 22 L ${c + 1.3} 44.4`}
            fill="none" stroke="#000000" strokeOpacity="0.3" strokeWidth="0.28" strokeLinecap="round"
          />
        )}

        {pillarTier >= 2 && (
          <>
            {/* Echinus flaring out to meet the abacus slab */}
            <path
              d={details.echinusPath}
              fill={pillarFill} stroke={pillarStyle.stroke} strokeWidth="0.3"
            />
            <rect {...details.abacus}
              fill={pillarFill} stroke={pillarStyle.stroke} strokeWidth="0.3" />
            <rect {...details.abacus} fill={`url(#${stepShadeId})`} />
            {pillarTier >= 3 && (
              <>
                {details.volutes.map(x => <circle key={`outer-${x}`} cx={x} cy="19.8" r="1.15" fill={pillarFill} stroke={pillarStyle.stroke} strokeWidth="0.3" />)}
                {details.volutes.map(x => <circle key={`inner-${x}`} cx={x} cy="19.8" r="0.34" fill={pillarStyle.stroke} />)}
              </>
            )}
            {/* Base torus */}
            <rect {...details.base}
              fill={pillarFill} stroke={pillarStyle.stroke} strokeWidth="0.3" />
            <rect {...details.base} fill={`url(#${stepShadeId})`} />
          </>
        )}
      </g>
    );
  };

  const renderInnerColumn = (c, key) => {
    const d = getTempleInnerColumnShaft(c, pillarTier);
    return (
      <g key={key} opacity="0.72">
        <path d={d} fill={pillarFill} stroke={pillarStyle.stroke} strokeWidth="0.2" />
        <path d={d} fill={`url(#${columnShadeId})`} />
      </g>
    );
  };

  return (
    <div className={`${size === 'large' ? 'flame-container-large' : 'flame-container-small'} ${getFlameClass ? getFlameClass(weekRate) : ''}`}>
      <div className="shimmer-wrapper">
        <svg viewBox="0 0 60 60" className={className}>
          <defs>
            {/* Circular clip path for arrows background */}
            <clipPath id={arrowsClipId}>
              <circle cx="30" cy="30" r="28" />
            </clipPath>
            
            {/* Radial gradient mask for fading arrows at edges */}
            <radialGradient id={`${arrowsClipId}-fade`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="white" stopOpacity="1" />
              <stop offset="60%" stopColor="white" stopOpacity="1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </radialGradient>
            <mask id={`${arrowsClipId}-mask`}>
              <circle cx="30" cy="30" r="28" fill={`url(#${arrowsClipId}-fade)`} />
            </mask>

            {/* Volume-PR arrow assets: white-hot shaft gradient, restrained glow,
                and a reusable symbol with a heated tip so each arrow reads as
                driven metal rather than a flat decorative chevron. */}
            {hasArrowBackground && (
              <>
                {/* Shaft gradient: incandescent white core at the tip melts into
                    the color, then trails off transparent. Reads as a heated
                    metal sliver rather than a flat colored chevron. */}
                <linearGradient id={arrowGradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#ffffff" stopOpacity="1" />
                  <stop offset="18%"  stopColor="#ffffff" stopOpacity="1" />
                  <stop offset="38%"  stopColor={arrowColor} stopOpacity="1" />
                  <stop offset="72%"  stopColor={arrowColor} stopOpacity="0.55" />
                  <stop offset="100%" stopColor={arrowColor} stopOpacity="0" />
                </linearGradient>
                {/* Soft white sparkle halo placed at the arrow tip — the "ember". */}
                <radialGradient id={`${arrowGlowId}-spark`} cx="50%" cy="50%" r="50%">
                  <stop offset="0%"   stopColor="#ffffff" stopOpacity="1" />
                  <stop offset="35%"  stopColor="#ffffff" stopOpacity="0.85" />
                  <stop offset="70%"  stopColor={arrowColor} stopOpacity="0.4" />
                  <stop offset="100%" stopColor={arrowColor} stopOpacity="0" />
                </radialGradient>
                {/* Wide, bright glow: a strong outer halo merged with a tighter
                    inner bloom and the original sharp shape on top. The alpha
                    boost on the outer blur is what makes the surrounding pixels
                    look luminous instead of muddy. */}
                <filter id={arrowGlowId} x="-200%" y="-200%" width="500%" height="500%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="haloBlur" />
                  <feComponentTransfer in="haloBlur" result="halo">
                    <feFuncA type="linear" slope="1.6" />
                  </feComponentTransfer>
                  <feGaussianBlur in="SourceGraphic" stdDeviation="0.7" result="bloom" />
                  <feMerge>
                    <feMergeNode in="halo" />
                    <feMergeNode in="bloom" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                {/* Vertical trail that fades out behind the dart. */}
                <linearGradient id={arrowTrailId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.55" />
                  <stop offset="45%"  stopColor={arrowColor} stopOpacity="0.28" />
                  <stop offset="100%" stopColor={arrowColor} stopOpacity="0" />
                </linearGradient>
                <symbol id={arrowSymbolId} overflow="visible">
                  {/* Sparkle ember sitting just past the tip — sells the
                      "spark trailing upward" read. Sized relative to the shaft. */}
                  <circle
                    cx="0"
                    cy={-arrowStyle.length * 0.55}
                    r={arrowStyle.width * 0.95}
                    fill={`url(#${arrowGlowId}-spark)`}
                  />
                  {/* Slipstream behind the dart so it reads as travelling, not floating. */}
                  <path
                    d={`M ${-arrowStyle.width * 0.11} ${-arrowStyle.length * 0.2} L ${arrowStyle.width * 0.11} ${-arrowStyle.length * 0.2} L 0 ${arrowStyle.length * 1.5} Z`}
                    fill={`url(#${arrowTrailId})`}
                  />
                  <path
                    d={arrowPath}
                    fill={`url(#${arrowGradId})`}
                  />
                </symbol>
              </>
            )}

            {hasShimmer && centralShape && (
              <>
                {/* Primary sweep. It rips across the totem and then waits off the
                    far edge, so the surface reads as catching the light rather
                    than as a highlight riding a conveyor belt. Both ends of the
                    cycle sit fully off the shape, so the restart is invisible. */}
                <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0.26" stopColor={glintStopColor} stopOpacity="0" />
                  <stop offset="0.44" stopColor={glintStopColor} stopOpacity="0.55" />
                  <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.85" />
                  <stop offset="0.56" stopColor={glintStopColor} stopOpacity="0.55" />
                  <stop offset="0.74" stopColor={glintStopColor} stopOpacity="0" />
                  <animate attributeName="x1" values="-150%;120%;120%" keyTimes="0;0.5;1"
                    calcMode="spline" keySplines="0.45 0.05 0.25 1;0 0 1 1"
                    dur="4.4s" repeatCount="1" />
                  <animate attributeName="x2" values="-50%;220%;220%" keyTimes="0;0.5;1"
                    calcMode="spline" keySplines="0.45 0.05 0.25 1;0 0 1 1"
                    dur="4.4s" repeatCount="1" />
                </linearGradient>
                {/* A second, slower pass on a different axis gives the badge a
                    layered entrance highlight without keeping the renderer awake. */}
                <linearGradient id={glint2Id} x1="0%" y1="0%" x2="70%" y2="100%">
                  <stop offset="0.34" stopColor={glintStopColor} stopOpacity="0" />
                  <stop offset="0.5" stopColor={glintStopColor} stopOpacity="0.32" />
                  <stop offset="0.66" stopColor={glintStopColor} stopOpacity="0" />
                  <animate attributeName="x1" values="-160%;120%;120%" keyTimes="0;0.62;1"
                    calcMode="spline" keySplines="0.5 0 0.3 1;0 0 1 1"
                    dur="6.9s" repeatCount="1" />
                  <animate attributeName="x2" values="-90%;190%;190%" keyTimes="0;0.62;1"
                    calcMode="spline" keySplines="0.5 0 0.3 1;0 0 1 1"
                    dur="6.9s" repeatCount="1" />
                </linearGradient>
                {hasPremiumSpecular && (
                  /* A restrained hotspot moves across gold/obsidian like a hard
                     machined surface; no decorative star particles. */
                  <radialGradient id={specularId} cx="50%" cy="50%" r="46%"
                    gradientUnits="objectBoundingBox">
                    <stop offset="0" stopColor="#ffffff" stopOpacity="0.42" />
                    <stop offset="0.45" stopColor={isForgedObsidian ? '#d7dde4' : '#fff6d8'} stopOpacity="0.11" />
                    <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
                    <animate attributeName="cx" values="32%;70%;46%;32%" keyTimes="0;0.34;0.68;1"
                      calcMode="spline" keySplines="0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 0.6 1"
                      dur="9.3s" repeatCount="1" />
                    <animate attributeName="cy" values="34%;30%;68%;34%" keyTimes="0;0.34;0.68;1"
                      calcMode="spline" keySplines="0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 0.6 1"
                      dur="9.3s" repeatCount="1" />
                  </radialGradient>
                )}
              </>
            )}
            
            {/* Temple Gradients */}
            {hasTemple && (
              <>
                {/* Foundation gradient - black granite with a cold steel face */}
                <linearGradient id={`${templeGradientId}-foundation`} x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#3a414a" />
                  <stop offset="42%" stopColor="#161a20" />
                  <stop offset="100%" stopColor="#07090c" />
                </linearGradient>
                {/* Pillar gradient - forged steel */}
                <linearGradient id={`${templeGradientId}-pillar`} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#c8ced6" />
                  <stop offset="24%" stopColor="#66717d" />
                  <stop offset="52%" stopColor="#20262e" />
                  <stop offset="78%" stopColor="#8b949f" />
                  <stop offset="100%" stopColor="#343b45" />
                </linearGradient>
                {/* Apex roof - black stone, steel and forged gold */}
                <linearGradient id={`${templeGradientId}-roof`} x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#080a0d" />
                  <stop offset="38%" stopColor="#252b33" />
                  <stop offset="67%" stopColor="#80651f" />
                  <stop offset="100%" stopColor="#d6ad3b" />
                </linearGradient>
                {/* Perfect roof - platinum edge over the same obsidian mass */}
                <linearGradient id={`${templeGradientId}-roof-perfect`} x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#0a0c10" />
                  <stop offset="40%" stopColor="#343b45" />
                  <stop offset="72%" stopColor="#9aa4af" />
                  <stop offset="100%" stopColor="#f8fafc" />
                </linearGradient>
                {/* Restrained forged-metal sheen; no aura extension. */}
                <filter id={`${templeGradientId}-forged-sheen`}>
                  <feComponentTransfer>
                    <feFuncR type="linear" slope="1.08" />
                    <feFuncG type="linear" slope="1.08" />
                    <feFuncB type="linear" slope="1.08" />
                  </feComponentTransfer>
                </filter>
              </>
            )}

            {/* Medallion: a lit interior that gives the totem a stage to sit on. */}
            <radialGradient id={discFaceId} cx="50%" cy="36%" r="76%">
              <stop offset="0%" stopColor="#22304d" />
              <stop offset="52%" stopColor="#141d31" />
              <stop offset="100%" stopColor="#060a14" />
            </radialGradient>

            {/* Rim metal. The sweep of stops reads as a machined bevel. On a
                shimmer tier it makes one seamless entrance pass around the ring,
                then settles so the badge does not repaint the app indefinitely. */}
            <linearGradient id={rimMetalId} x1="8%" y1="0%" x2="92%" y2="100%" spreadMethod="repeat">
              {[...rimStops, rimStops[0]].map((c, i, arr) => (
                <stop key={`rim-${i}`} offset={`${(i / (arr.length - 1)) * 100}%`} stopColor={c} />
              ))}
              {hasShimmer && (
                <animateTransform
                  attributeName="gradientTransform"
                  type="translate"
                  from="0 0"
                  to="0.84 1"
                  dur={isForgedObsidian ? '7s' : '9s'}
                  repeatCount="1"
                />
              )}
            </linearGradient>

            {/* A single specular arc so the rim catches light from the top-left. */}
            <linearGradient id={rimSheenId} x1="0%" y1="0%" x2="55%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.5" />
              <stop offset="30%" stopColor="#ffffff" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>

            {/* Darkens the interior toward the rim so the totem stays the focus. */}
            <radialGradient id={discVignetteId} cx="50%" cy="50%" r="50%">
              <stop offset="62%" stopColor="#000000" stopOpacity="0" />
              <stop offset="100%" stopColor="#000000" stopOpacity="0.6" />
            </radialGradient>

            {/* Everything inside the medallion is trimmed by the rim. */}
            <clipPath id={discClipId}>
              <circle cx="30" cy="30" r="26.4" />
            </clipPath>

            {/* Ground plane. Deliberately runs past the clip so the rim trims it
                into a horizon instead of slicing corners off the staircase. */}
            <linearGradient id={groundId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#2b3d5c" />
              <stop offset="35%" stopColor="#16223a" />
              <stop offset="100%" stopColor="#05090f" />
            </linearGradient>

            {/* Light from the upper left. These three overlays are painted on top
                of whatever tier fill is active, so every tier gets the same
                modelling without duplicating a palette per material. */}
            <linearGradient id={columnShadeId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.30" />
              <stop offset="26%" stopColor="#ffffff" stopOpacity="0.07" />
              <stop offset="50%" stopColor="#000000" stopOpacity="0" />
              <stop offset="78%" stopColor="#000000" stopOpacity="0.26" />
              <stop offset="100%" stopColor="#000000" stopOpacity="0.44" />
            </linearGradient>
            <linearGradient id={roofShadeId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.32" />
              <stop offset="45%" stopColor="#ffffff" stopOpacity="0.04" />
              <stop offset="55%" stopColor="#000000" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#000000" stopOpacity="0.36" />
            </linearGradient>
            <linearGradient id={stepShadeId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.36" />
              <stop offset="20%" stopColor="#ffffff" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#000000" stopOpacity="0.38" />
            </linearGradient>
          </defs>

          {/* Everything the badge is actually made of. The shimmer pass at the
              end masks itself to the luminance of this group, so the dark void
              between the temple pieces never catches the light. */}
          <g id={contentId}>

          {/* Medallion interior, drawn first so the whole badge has one opaque
              silhouette — that is what turns the flame glow into a clean halo
              instead of a bloom around every individual shape. */}
          <circle cx="30" cy="30" r="26.4" fill={`url(#${discFaceId})`} />

          {/* The temple scene, scaled to sit comfortably inside the rim. */}
          <g clipPath={`url(#${discClipId})`}>
            <g transform="translate(30 30) scale(0.86) translate(-30 -30)">
          
          {/* === LAYER 0: Ground. It runs past the clip on purpose so the rim
              reads as a horizon, rather than shearing the corners off a
              staircase that widens exactly where the circle narrows. === */}
          {hasTemple && (
            <g className="temple-ground">
              <path d="M -6 50.2 H 66 V 66 H -6 Z" fill={`url(#${groundId})`} />
              <path d="M -6 50.2 H 66" fill="none" stroke="rgba(150,185,235,0.22)" strokeWidth="0.4" />
            </g>
          )}

          {/* === LAYER 1: Temple Pillars (bottom layer - columns rise from the stylobate) === */}
          {hasTemple && pillarTier >= 1 && (
            <g className={`badge-nutrition-temple-pillars ${isTempleComplete ? 'temple-complete' : ''}`}
               style={{ filter: isTempleComplete ? `url(#${templeGradientId}-forged-sheen)` : 'none' }}>
              <g className="temple-pillars" style={{ filter: pillarStyle.glow }}>
                {pillarTier >= 3 && renderInnerColumn(TEMPLE_COLUMN_CENTERS.inner[0], 'inner-left')}
                {pillarTier >= 3 && renderInnerColumn(TEMPLE_COLUMN_CENTERS.inner[1], 'inner-right')}
                {renderColumn(TEMPLE_COLUMN_CENTERS.outer[0], 'col-left')}
                {renderColumn(TEMPLE_COLUMN_CENTERS.outer[1], 'col-right')}
              </g>
            </g>
          )}
          
          {/* === LAYER 2: Temple Roof (on top of pillars) === */}
          {hasTemple && roofTier >= 1 && (
            <g className={`badge-nutrition-temple-roof ${isTempleComplete ? 'temple-complete' : ''}`}
               style={{ filter: isTempleComplete ? `url(#${templeGradientId}-forged-sheen)` : 'none' }}>
              <g className="temple-roof" style={{ filter: roofStyle.glow }}>
                {/* Entablature. The underside is pinned to y=18.5 at every tier so
                    the capitals always meet it; only the top edge grows. */}
                {(() => {
                  const beam = getTempleRoofBeam(roofTier);
                  const { x: x0, top, width: w } = beam;
                  const fill = roofTier >= 12
                    ? `url(#${templeGradientId}-roof-perfect)`
                    : roofTier >= 11 ? `url(#${templeGradientId}-roof)` : roofStyle.fill;
                  return (
                    <>
                      <rect x={x0} y={top} width={w} height={beam.height}
                        fill={fill} stroke={roofStyle.stroke} strokeWidth="0.3"
                        rx={roofTier >= 8 ? '0.4' : '0'} />
                      {/* Cornice lip: a thin overhanging slab reads as depth far
                          better than making the whole beam taller. */}
                      {beam.cornice && (
                        <rect x={beam.cornice.x} y={beam.cornice.y} width={beam.cornice.width} height={beam.cornice.height} rx={beam.cornice.rx}
                          fill={fill} stroke={roofStyle.stroke} strokeWidth="0.3" />
                      )}
                      {/* Triglyph ticks across the frieze */}
                      {roofTier >= 7 && (
                        <g stroke={roofStyle.stroke} strokeWidth="0.35" opacity="0.55">
                          {[0, 1, 2, 3, 4, 5, 6].map(i => {
                            const gx = x0 + 2.5 + i * ((w - 5) / 6);
                            return <line key={`tri-${i}`} x1={gx} y1={top + 1.4} x2={gx} y2={BEAM_BOTTOM - 0.5} />;
                          })}
                        </g>
                      )}
                      <rect x={x0} y={top} width={w} height={beam.height}
                        rx={roofTier >= 8 ? '0.4' : '0'} fill={`url(#${stepShadeId})`} />
                    </>
                  );
                })()}
                
                {/* Wooden A-frame (tiers 1-2): triangular frame with ridge beam */}
                {roofTier >= 1 && roofTier < 3 && (
                  <>
                    {/* Main A-frame: two sloped rafters meeting at a peak */}
                    <path
                      d={roofTier >= 2
                        ? "M 10 16 L 30 7 L 50 16"
                        : "M 12 16.6 L 30 8.5 L 48 16.6"}
                      fill="none"
                      stroke={roofStyle.fill}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.9"
                    />
                    {/* Ridge beam highlight (lighter inner stroke for depth) */}
                    <path
                      d={roofTier >= 2
                        ? "M 11 16 L 30 7.6 L 49 16"
                        : "M 13 16.6 L 30 9 L 47 16.6"}
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth="0.4"
                      strokeLinecap="round"
                      opacity="0.22"
                    />
                  </>
                )}
                {/* Horizontal tie beam + purlins + king post (tier 2) */}
                {roofTier >= 2 && roofTier < 3 && (
                  <>
                    <line x1="15" y1="13.5" x2="45" y2="13.5"
                      stroke={roofStyle.fill} strokeWidth="1" strokeLinecap="round" opacity="0.75" />
                    {/* Left purlin */}
                    <line x1="18" y1="12.4" x2="24" y2="9.4"
                      stroke={roofStyle.fill} strokeWidth="0.8" strokeLinecap="round" opacity="0.6" />
                    {/* Right purlin */}
                    <line x1="42" y1="12.4" x2="36" y2="9.4"
                      stroke={roofStyle.fill} strokeWidth="0.8" strokeLinecap="round" opacity="0.6" />
                    {/* King post */}
                    <line x1="30" y1="13.5" x2="30" y2="7.4"
                      stroke={roofStyle.fill} strokeWidth="0.9" strokeLinecap="round" opacity="0.7" />
                  </>
                )}
                
                {/* Thatch/Wood covering (tiers 3-6): triangular fill growing from center */}
                {roofTier >= 3 && roofTier < 7 && (
                  <>
                    <path d={pedimentPath} fill={roofStyle.fill} stroke={roofStyle.stroke}
                      strokeWidth="0.5" strokeLinejoin="round" />
                    <path d={pedimentPath} fill={`url(#${roofShadeId})`} />
                  </>
                )}
                {/* Wood grain / plank lines for texture */}
                {roofTier >= 3 && roofTier < 7 && (
                  <>
                    {/* Ridge beam along the peak */}
                    <line 
                      x1={roofTier >= 5 ? "22" : roofTier >= 4 ? "24" : "26"} 
                      y1={roofTier >= 5 ? "9" : roofTier >= 4 ? "10" : "11"} 
                      x2={roofTier >= 5 ? "38" : roofTier >= 4 ? "36" : "34"} 
                      y2={roofTier >= 5 ? "9" : roofTier >= 4 ? "10" : "11"} 
                      stroke={roofStyle.stroke} strokeWidth="0.6" opacity="0.6" />
                    {/* Horizontal plank lines */}
                    <line 
                      x1={roofTier >= 5 ? "14" : "18"} y1="14" 
                      x2={roofTier >= 5 ? "46" : "42"} y2="14" 
                      stroke={roofStyle.stroke} strokeWidth="0.4" opacity="0.5" />
                    {roofTier >= 4 && (
                      <line 
                        x1={roofTier >= 5 ? "18" : "22"} y1="12" 
                        x2={roofTier >= 5 ? "42" : "38"} y2="12" 
                        stroke={roofStyle.stroke} strokeWidth="0.3" opacity="0.45" />
                    )}
                    {/* Diagonal wood grain on left slope */}
                    {roofTier >= 4 && (
                      <>
                        <line x1="18" y1="15" x2="24" y2="11" stroke={roofStyle.stroke} strokeWidth="0.25" opacity="0.3" />
                        <line x1="22" y1="15" x2="27" y2="11.5" stroke={roofStyle.stroke} strokeWidth="0.25" opacity="0.25" />
                      </>
                    )}
                    {/* Diagonal wood grain on right slope */}
                    {roofTier >= 4 && (
                      <>
                        <line x1="42" y1="15" x2="36" y2="11" stroke={roofStyle.stroke} strokeWidth="0.25" opacity="0.3" />
                        <line x1="38" y1="15" x2="33" y2="11.5" stroke={roofStyle.stroke} strokeWidth="0.25" opacity="0.25" />
                      </>
                    )}
                    {/* Highlight line along ridge for depth */}
                    {roofTier >= 6 && (
                      <line x1="24" y1="8.5" x2="36" y2="8.5" 
                        stroke="#c4a46a" strokeWidth="0.3" opacity="0.35" />
                    )}
                  </>
                )}
                
                {/* Stone/Marble covering (tiers 7-10): solid pediment with increasing refinement */}
                {roofTier >= 7 && roofTier < 11 && (
                  <>
                    <path d={pedimentPath} fill={roofStyle.fill} stroke={roofStyle.stroke}
                      strokeWidth="0.5" strokeLinejoin="round" />
                    {/* Recessed tympanum — the sculpted panel inside the gable */}
                    <path d={tympanumPath} fill="#000000" opacity="0.22" />
                    <path d={tympanumPath} fill="none" stroke={roofStyle.stroke} strokeWidth="0.3" opacity="0.7" />
                    <path d={pedimentPath} fill={`url(#${roofShadeId})`} />
                    {/* Raking cornice: the bright edge that catches the sky */}
                    <path d={pedimentPath} fill="none" stroke="#ffffff" strokeWidth="0.35"
                      strokeLinejoin="round" opacity="0.3" />
                  </>
                )}
                {/* Antefix ornaments running along the eaves */}
                {roofTier >= 9 && roofTier < 11 && (
                  <g fill={roofStyle.stroke} opacity="0.75">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                      <circle key={`antefix-${i}`} cx={8 + i * 5.5} cy="13.9" r="0.5" />
                    ))}
                  </g>
                )}
                
                {/* Apex monument (tier 11+): black stone with forged-metal edge */}
                {roofTier >= 11 && (
                  <>
                    <path d={pedimentPath}
                      fill={`url(#${templeGradientId}-${roofTier >= 12 ? 'roof-perfect' : 'roof'})`}
                      stroke={roofStyle.stroke} strokeWidth="0.55" strokeLinejoin="round" />
                    <path d={tympanumPath} fill="#000000" opacity="0.32" />
                    <g stroke={roofTier >= 12 ? '#f8fafc' : '#d6ad3b'} strokeWidth="0.25" opacity="0.46">
                      {[-8, -4, 0, 4, 8].map(dx => (
                        <line key={`ray-${dx}`} x1="30" y1="13.2" x2={30 + dx} y2="7.4" />
                      ))}
                    </g>
                    <path d={tympanumPath} fill="none" stroke={roofTier >= 12 ? '#d7dde4' : '#b99332'} strokeWidth="0.3" opacity="0.8" />
                    <path d={pedimentPath} fill={`url(#${roofShadeId})`} />
                    <path d={pedimentPath} fill="none" stroke={roofTier >= 12 ? '#f8fafc' : '#d6ad3b'} strokeWidth="0.4"
                      strokeLinejoin="round" opacity="0.62" />
                    <g fill={roofTier >= 12 ? '#d7dde4' : '#b99332'} opacity="0.82">
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                        <circle key={`apex-antefix-${i}`} cx={7 + i * 5.1} cy="13.9" r="0.5" />
                      ))}
                    </g>
                  </>
                )}

                {/* Static apex and corner fasteners replace pulsing magical ornaments. */}
                {roofTier >= 11 && (
                  <>
                    <path d="M 30 0.9 L 32.2 3.1 L 30 5.3 L 27.8 3.1 Z"
                      fill={roofTier >= 12 ? '#f8fafc' : '#d6ad3b'} stroke="#11161d" strokeWidth="0.3" />
                    <circle cx="4.6" cy="14.2" r="0.85" fill={roofTier >= 12 ? '#c8ced6' : '#b99332'} />
                    <circle cx="55.4" cy="14.2" r="0.85" fill={roofTier >= 12 ? '#c8ced6' : '#b99332'} />
                  </>
                )}
              </g>
            </g>
          )}
          
          {/* === LAYER 3: Animated Arrows BACKGROUND for Volume PRs (behind the animal) === */}
          {hasArrowBackground && (
            <g
              mask={`url(#${arrowsClipId}-mask)`}
              style={{
                filter: `url(#${arrowGlowId})`,
                opacity: 1,
                // Additively lighten anything beneath — the animal/totem will
                // visibly glow as a spark passes over it.
                mixBlendMode: 'screen',
                isolation: 'isolate'
              }}
            >
              {arrowPositions.filter((_, i) => i % 2 === 0).map((pos, i) => (
                <g
                  key={`arrow-bg-${i}`}
                  transform={`translate(${pos.x} ${pos.startY})`}
                >
                  <animateTransform
                    attributeName="transform"
                    type="translate"
                    values={`${pos.x} ${pos.startY}; ${pos.x} -10`}
                    keyTimes="0;1"
                    calcMode="spline"
                    keySplines="0.45 0 0.55 1"
                    dur={`${arrowConfig.duration}s`}
                    begin={`${pos.delay}s`}
                    repeatCount="1"
                  />
                  <animate
                    attributeName="opacity"
                    values="0;0.95;0.95;0"
                    keyTimes="0;0.18;0.78;1"
                    dur={`${arrowConfig.duration}s`}
                    begin={`${pos.delay}s`}
                    repeatCount="1"
                  />
                  <use href={`#${arrowSymbolId}`} />
                </g>
              ))}
            </g>
          )}
          
          {/* === LAYER 4: Temple Foundation (above arrows - the floor) === */}
          {hasTemple && foundationTier >= 1 && (
            <g className="badge-nutrition-temple-foundation"
               style={{ filter: isTempleComplete ? `url(#${templeGradientId}-forged-sheen)` : 'none' }}>
              <g className="temple-foundation" style={{ filter: foundationStyle.glow }}>
                {/* Lower step. Its ends stop just inside the rim at y=50.3, which
                    is the widest a step can be down here and still be a step
                    rather than a sliced rectangle. */}
                {foundationTier >= 2 && (
                  <>
                    <rect {...TEMPLE_FOUNDATION_RECTS.lower}
                      fill={foundationFill} stroke={foundationStyle.stroke} strokeWidth="0.4" />
                    <rect {...TEMPLE_FOUNDATION_RECTS.lower} fill={`url(#${stepShadeId})`} />
                  </>
                )}
                {/* Stylobate — the platform every column lands on. */}
                <rect {...TEMPLE_FOUNDATION_RECTS.stylobate}
                  fill={foundationFill} stroke={foundationStyle.stroke} strokeWidth="0.4" />
                <rect {...TEMPLE_FOUNDATION_RECTS.stylobate} fill={`url(#${stepShadeId})`} />
                {/* Moulding course along the tread */}
                {foundationTier >= 3 && (
                  <rect x="9.7" y="45.75" width="40.6" height="0.5" rx="0.25" fill="#ffffff" opacity="0.26" />
                )}
                {/* Decorative edge lines for top tier */}
                {foundationTier >= 4 && (
                  <>
                    <line x1="9" y1="45.85" x2="51" y2="45.85" stroke="#d6ad3b" strokeWidth="0.4" strokeOpacity="0.85" />
                    <line x1="7" y1="48.05" x2="53" y2="48.05" stroke="#d6ad3b" strokeWidth="0.35" strokeOpacity="0.6" />
                  </>
                )}
              </g>
            </g>
          )}
          
          {/* === LAYER 5: Central shape (animal) and stars (top layer) === */}
          <g
            transform={totemTransform}
            className={isForgedObsidian ? 'forged-obsidian-totem' : undefined}
            style={{ filter: isForgedObsidian
              ? 'drop-shadow(0 1px 1px rgba(214,173,59,0.48))'
              : 'none' }}
          >
            {centralShape}
          </g>

          {/* Long-term stars keep their original unscaled badge coordinates. */}
          <g className="badge-long-term-stars">
            {stars.map((star, i) => (
              <polygon key={`star-${i}`} points={star.points} fill="#ffd700" transform={star.transform} />
            ))}
          </g>

          {/* === LAYER 7: Animated Arrows FOREGROUND for Volume PRs (in front of everything) === */}
          {hasArrowBackground && (
            <g
              mask={`url(#${arrowsClipId}-mask)`}
              style={{
                filter: `url(#${arrowGlowId})`,
                opacity: 1,
                // `screen` so each spark visibly lights up the totem/badge as
                // it streaks past — no more ghostly dark arrows.
                mixBlendMode: 'screen',
                isolation: 'isolate'
              }}
            >
              {arrowPositions.filter((_, i) => i % 2 === 1).map((pos, i) => (
                <g
                  key={`arrow-fg-${i}`}
                  transform={`translate(${pos.x} ${pos.startY})`}
                >
                  <animateTransform
                    attributeName="transform"
                    type="translate"
                    values={`${pos.x} ${pos.startY}; ${pos.x} -10`}
                    keyTimes="0;1"
                    calcMode="spline"
                    keySplines="0.4 0 0.55 1"
                    dur={`${arrowConfig.duration}s`}
                    begin={`${pos.delay}s`}
                    repeatCount="1"
                  />
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0"
                    keyTimes="0;0.15;0.78;1"
                    dur={`${arrowConfig.duration}s`}
                    begin={`${pos.delay}s`}
                    repeatCount="1"
                  />
                  <use href={`#${arrowSymbolId}`} transform="scale(1.18)" />
                </g>
              ))}
            </g>
          )}
            </g>
          </g>

          {/* Medallion rim. The flame tier glows from here (see App.css) rather
              than from every shape in the badge. */}
          <circle cx="30" cy="30" r="26.4" fill={`url(#${discVignetteId})`} pointerEvents="none" />
          <g className="badge-rim">
            <circle cx="30" cy="30" r="27.6" fill="none" stroke="rgba(0,0,0,0.55)" strokeWidth="1.4" />
            <circle cx="30" cy="30" r="27.6" fill="none" stroke={`url(#${rimMetalId})`} strokeWidth="2.2" />
            {/* Obsidian gets one restrained forged-gold registration seam. */}
            {isForgedObsidian && centralShape && (
              <circle cx="30" cy="30" r="26.65" fill="none" stroke="#d6ad3b" strokeWidth="0.45" opacity="0.88" />
            )}
            <circle cx="30" cy="30" r="27.6" fill="none" stroke={`url(#${rimSheenId})`} strokeWidth="2.2" />
            <circle cx="30" cy="30" r="26.4" fill="none" stroke="rgba(0,0,0,0.45)" strokeWidth="0.5" />
            <circle cx="30" cy="30" r="28.8" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.4" />
          </g>

          </g>

          {/* Shimmer pass over the finished medallion. Everything beneath is
              already composited, so a single sweep crosses temple, totem and rim
              as one surface instead of each shape catching the light on its own. */}
          {hasShimmer && centralShape && (
            <>
              {/* A luminance mask of the badge itself. The void between the
                  temple pieces is nearly black, so it resolves to ~0 and stays
                  matte, while stone, totem and rim catch the sweep in
                  proportion to how lit they already are. */}
              <mask id={shineMaskId} maskUnits="userSpaceOnUse" x="0" y="0" width="60" height="60">
                <use href={`#${contentId}`} />
              </mask>

              {/* Clip and mask go on each layer rather than a wrapping group: a
                  group carrying either one forms a stacking context, which would
                  isolate these blend modes from the badge underneath. */}
              <rect x="0" y="0" width="60" height="60" pointerEvents="none"
                mask={`url(#${shineMaskId})`}
                fill={`url(#${gradientId})`} style={{ mixBlendMode: 'overlay' }} />
              <rect x="0" y="0" width="60" height="60" pointerEvents="none"
                mask={`url(#${shineMaskId})`}
                fill={`url(#${glint2Id})`} style={{ mixBlendMode: 'screen' }} />
              {hasPremiumSpecular && (
                <rect x="0" y="0" width="60" height="60" pointerEvents="none"
                  mask={`url(#${shineMaskId})`}
                  fill={`url(#${specularId})`} style={{ mixBlendMode: 'screen' }} />
              )}
            </>
          )}
        </svg>
      </div>
    </div>
  );
};

export default Badge;